"use client";

import { ilikeContainsFilter } from "@/lib/postgrest-filter";
import { supabase } from "@/lib/supabase";
import type { DbMeetingRecording } from "@/types/db";
import { useCallback, useEffect, useState } from "react";

type MeetingTeamRow = {
  name: string;
};

// Both consumers (meetings list page, previous-meeting picker in the minutes
// panel) only render list metadata — heavy columns like `transcript` and
// `minutes` are lazy-fetched per recording when actually needed.
export type MeetingRecordingListItem = Pick<
  DbMeetingRecording,
  "id" | "title" | "meeting_context" | "status" | "created_at" | "team_name"
>;

type MeetingRecordingRow = Omit<MeetingRecordingListItem, "team_name"> & {
  teams: MeetingTeamRow | MeetingTeamRow[] | null;
};

function normalise(rows: MeetingRecordingRow[] | null): MeetingRecordingListItem[] {
  if (!rows) {
    return [];
  }

  return rows.map(({ teams, ...recording }) => ({
    ...recording,
    team_name: Array.isArray(teams)
      ? (teams[0]?.name ?? null)
      : (teams?.name ?? null),
  }));
}

export type UseMeetingRecordingsReturn = {
  meetings: MeetingRecordingListItem[];
  loading: boolean;
  refresh: () => void;
  updateMeetingTitle: (meetingId: string, title: string) => Promise<boolean>;
  deleteMeeting: (meetingId: string) => Promise<boolean>;
};

// ── Cursor pagination ──────────────────────────────────────────────────────────
// Keyset cursor for the (created_at DESC, id DESC) ordering — matches
// idx_meeting_recordings_created_at_id so each page is a single index scan.

export type MeetingsCursor = { createdAt: string; id: string };

export function meetingCursorFromRow(meeting: MeetingRecordingListItem): MeetingsCursor {
  return { createdAt: meeting.created_at, id: meeting.id };
}

export async function fetchMeetingsPage(
  cursor: MeetingsCursor | null,
  pageSize: number,
  search?: string
): Promise<MeetingRecordingListItem[]> {
  let query = supabase
    .from("meeting_recordings")
    .select(`
      id,
      title,
      meeting_context,
      status,
      created_at,
      teams (
        name
      )
    `)
    .order("created_at", { ascending: false })
    .order("id", { ascending: false })
    .limit(pageSize);

  const trimmedSearch = search?.trim();
  if (trimmedSearch) {
    query = query.or(
      [ilikeContainsFilter("title", trimmedSearch), ilikeContainsFilter("meeting_context", trimmedSearch)].join(",")
    );
  }

  if (cursor) {
    query = query.or(
      `created_at.lt.${cursor.createdAt},and(created_at.eq.${cursor.createdAt},id.lt.${cursor.id})`
    );
  }

  const { data, error } = await query;

  if (error) {
    console.error("[fetchMeetingsPage] fetch error:", error.message, { code: error.code, details: error.details, hint: error.hint });
    return [];
  }

  return normalise((data as MeetingRecordingRow[] | null) ?? null);
}

export async function renameMeetingRow(meetingId: string, title: string): Promise<boolean> {
  const nextTitle = title.trim().slice(0, 200);
  if (!nextTitle) return false;

  const { error } = await supabase
    .from("meeting_recordings")
    .update({
      title: nextTitle,
      title_manually_edited: true,
      updated_at: new Date().toISOString(),
    })
    .eq("id", meetingId);

  if (error) {
    console.error("[renameMeetingRow] error:", error);
    return false;
  }

  return true;
}

export async function deleteMeetingRow(meetingId: string): Promise<boolean> {
  const { error } = await supabase.from("meeting_recordings").delete().eq("id", meetingId);

  if (error) {
    console.error("[deleteMeetingRow] error:", error);
    return false;
  }

  return true;
}

export function useMeetingRecordings(): UseMeetingRecordingsReturn {
  const [meetings, setMeetings] = useState<MeetingRecordingListItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchMeetings = useCallback(async () => {
    setLoading(true);

    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;

    if (!user) {
      setMeetings([]);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from("meeting_recordings")
      .select(`
        id,
        title,
        meeting_context,
        status,
        created_at,
        teams (
          name
        )
      `)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("[useMeetingRecordings] fetch error:", error.message, { code: error.code, details: error.details, hint: error.hint });
      setMeetings([]);
      setLoading(false);
      return;
    }

    setMeetings(normalise((data as MeetingRecordingRow[] | null) ?? null));
    setLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;

    queueMicrotask(() => {
      if (!cancelled) {
        void fetchMeetings();
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fetchMeetings]);

  const updateMeetingTitle = useCallback(async (meetingId: string, title: string) => {
    const nextTitle = title.trim().slice(0, 200);
    if (!nextTitle) return false;

    setMeetings((current) =>
      current.map((meeting) =>
        meeting.id === meetingId ? { ...meeting, title: nextTitle } : meeting
      )
    );

    const { error } = await supabase
      .from("meeting_recordings")
      .update({
        title: nextTitle,
        title_manually_edited: true,
        updated_at: new Date().toISOString(),
      })
      .eq("id", meetingId);

    if (error) {
      console.error("[useMeetingRecordings] updateMeetingTitle error:", error);
      return false;
    }

    return true;
  }, []);

  const deleteMeeting = useCallback(async (meetingId: string) => {
    const { error } = await supabase
      .from("meeting_recordings")
      .delete()
      .eq("id", meetingId);

    if (error) {
      console.error("[useMeetingRecordings] deleteMeeting error:", error);
      return false;
    }

    setMeetings((current) => current.filter((meeting) => meeting.id !== meetingId));
    return true;
  }, []);

  return { meetings, loading, refresh: fetchMeetings, updateMeetingTitle, deleteMeeting };
}
